import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, ArrowLeft, Save, X, Package, Eye } from "lucide-react";
import { LeafGlyph } from "@/components/BotanicalBackdrop";
import { HKMonogram } from "@/components/BrandMark";
import { useProducts } from "@/context/ProductsContext";
import usePageTitle from "@/hooks/usePageTitle";
import AdminLayout from "@/components/AdminLayout";
import { ImageField, ImageGalleryField } from "@/components/MediaUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hk_admin_pw";

const STATUS_OPTIONS = [
  { value: "draft",        label: "Draft" },
  { value: "coming-soon",  label: "Coming Soon" },
  { value: "available",    label: "Available Now" },
  { value: "out-of-stock", label: "Out of Stock" },
  { value: "discontinued", label: "Discontinued" },
];

const emptyProduct = {
  slug: "",
  name: "",
  tagline: "",
  shortLine: "",
  packSize: "",
  priceDisplay: "",
  price: null,
  offer: "",
  offerDescription: "",
  status: "coming-soon",
  statusLabel: "Coming Soon",
  heroImage: "",
  overview: "",
  philosophy: "",
  storage: "",
  benefits: [],
  howToUse: [],
  faqs: [],
  gallery: [],
  homepageDisplay: true,
  featured: false,
};

const slugify = (name) =>
  name.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60);

/* ------------------------------------------------------------------ */
/*  Product form                                                       */
/* ------------------------------------------------------------------ */
const ProductForm = ({ initial, isEdit, onSave, onCancel, busy }) => {
  const [form, setForm] = useState({ ...emptyProduct, ...initial });

  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const onNameChange = (e) => {
    const name = e.target.value;
    setForm((f) => ({ ...f, name, slug: isEdit ? f.slug : (f.slug || slugify(name)) }));
  };

  const updateBenefit = (idx, key, value) => {
    setForm((f) => {
      const next = [...(f.benefits || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...f, benefits: next };
    });
  };
  const addBenefit = () => setForm((f) => ({ ...f, benefits: [...(f.benefits || []), { title: "", body: "" }] }));
  const removeBenefit = (idx) => setForm((f) => ({ ...f, benefits: (f.benefits || []).filter((_, i) => i !== idx) }));

  const updateFaq = (idx, key, value) => {
    setForm((f) => {
      const next = [...(f.faqs || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...f, faqs: next };
    });
  };
  const addFaq = () => setForm((f) => ({ ...f, faqs: [...(f.faqs || []), { q: "", a: "" }] }));
  const removeFaq = (idx) => setForm((f) => ({ ...f, faqs: (f.faqs || []).filter((_, i) => i !== idx) }));

  const updateStep = (idx, key, value) => {
    setForm((f) => {
      const next = [...(f.howToUse || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...f, howToUse: next };
    });
  };
  const addStep = () => setForm((f) => ({ ...f, howToUse: [...(f.howToUse || []), { step: `0${(f.howToUse || []).length + 1}`, label: "", body: "" }] }));
  const removeStep = (idx) => setForm((f) => ({ ...f, howToUse: (f.howToUse || []).filter((_, i) => i !== idx) }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) {
      toast.error("Product name and slug are required");
      return;
    }
    // sync statusLabel with status for the badge
    const status = form.status;
    const statusLabel = STATUS_OPTIONS.find((s) => s.value === status)?.label || form.statusLabel;
    const priceNum = form.price === "" || form.price === null || form.price === undefined ? null : Number(form.price);
    onSave({
      ...form,
      statusLabel,
      price: Number.isFinite(priceNum) ? priceNum : null,
      slug: slugify(form.slug),
    });
  };

  const Field = ({ label, hint, children }) => (
    <label className="block">
      <div className="label-eyebrow !text-[10px] mb-1.5">{label}</div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-[var(--hk-ink-3)]">{hint}</div>}
    </label>
  );

  const inputCls =
    "w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--hk-orange)]/50 text-[14px]";

  return (
    <form onSubmit={submit} className="hk-card p-6 lg:p-8 space-y-6" data-testid="admin-product-form">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h3 className="serif text-[24px] text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>
          {isEdit ? "Edit product" : "Create a new product"}
        </h3>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel} className="hk-btn-ghost !py-2" data-testid="admin-product-cancel">
            <X size={14} /> Cancel
          </button>
          <button type="submit" disabled={busy} className="hk-btn-primary !py-2" data-testid="admin-product-save">
            <Save size={14} /> {busy ? "Saving…" : (isEdit ? "Update Product" : "Create Product")}
          </button>
        </div>
      </div>

      {/* Essentials */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Product name *">
          <input className={inputCls} value={form.name} onChange={onNameChange} required data-testid="admin-product-name-input" />
        </Field>
        <Field label="URL slug *" hint="Auto-generated from name — edit to customise the product URL">
          <input className={inputCls} value={form.slug} onChange={set("slug")} disabled={isEdit} data-testid="admin-product-slug-input" />
        </Field>
        <Field label="Tagline">
          <input className={inputCls} value={form.tagline || ""} onChange={set("tagline")} placeholder="e.g. Premium herbal granulated formulation" />
        </Field>
        <Field label="Short line (product card)">
          <input className={inputCls} value={form.shortLine || ""} onChange={set("shortLine")} placeholder="A one-line summary shown on cards" />
        </Field>
        <Field label="Pack size">
          <input className={inputCls} value={form.packSize || ""} onChange={set("packSize")} placeholder="e.g. 100 g · 30-day supply" />
        </Field>
        <Field label="Status *">
          <select className={inputCls} value={form.status} onChange={set("status")} data-testid="admin-product-status-select">
            {STATUS_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </Field>
        <Field label="Price display (as shown on card)">
          <input className={inputCls} value={form.priceDisplay || ""} onChange={set("priceDisplay")} placeholder="e.g. ₹1,299" />
        </Field>
        <Field label="Price (paise, for cart)" hint="e.g. 129900 for ₹1,299. Leave blank for coming-soon.">
          <input className={inputCls} type="number" value={form.price ?? ""} onChange={set("price")} />
        </Field>
        <Field label="Offer text">
          <input className={inputCls} value={form.offer || ""} onChange={set("offer")} placeholder="e.g. Buy 1 Get 1 FREE" />
        </Field>
        <Field label="Offer description">
          <input className={inputCls} value={form.offerDescription || ""} onChange={set("offerDescription")} placeholder="Optional supporting line" />
        </Field>
        <div className="md:col-span-2">
          <ImageField
            label="Hero / primary image"
            value={form.heroImage || ""}
            onChange={(url) => setForm((f) => ({ ...f, heroImage: url }))}
            aspect="square"
            testid="admin-product-hero"
            help="Also becomes the first thumbnail. Setting a Primary image in the gallery below auto-syncs here."
          />
        </div>
      </div>

      {/* ============ PRODUCT MEDIA / GALLERY EDITOR ============ */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-eyebrow">Product Media · Gallery</div>
            <p className="text-[12px] text-[var(--hk-ink-3)] mt-1">Drop images or click to upload. First image is Primary — it auto-becomes the hero. Reorder to control the thumbnail strip on the live product page.</p>
          </div>
        </div>
        <ImageGalleryField
          items={form.gallery || []}
          onChange={(next) => setForm((f) => ({
            ...f,
            gallery: next,
            heroImage: next[0]?.url || "",
          }))}
          testid="admin-product-gallery"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="SKU (stock keeping unit)"><input className={inputCls} value={form.sku || ""} onChange={set("sku")} placeholder="SBP-300ML" /></Field>
        <Field label="HSN code"><input className={inputCls} value={form.hsn || ""} onChange={set("hsn")} placeholder="21069090" /></Field>
        <Field label="GST rate (%)"><input className={inputCls} type="number" value={form.gst_rate ?? ""} onChange={set("gst_rate")} /></Field>
        <Field label="MRP (paise)"><input className={inputCls} type="number" value={form.mrp ?? ""} onChange={set("mrp")} /></Field>
      </div>

      <div className="grid md:grid-cols-4 gap-4" data-testid="product-logistics-fields">
        <Field label="Weight (g)" help="For courier rate & Shiprocket/Fship shipment"><input className={inputCls} type="number" value={form.weight_g ?? ""} onChange={(e) => set("weight_g")(e)} data-testid="product-weight" /></Field>
        <Field label="Length (cm)"><input className={inputCls} type="number" value={form.length_cm ?? ""} onChange={(e) => set("length_cm")(e)} data-testid="product-length" /></Field>
        <Field label="Breadth (cm)"><input className={inputCls} type="number" value={form.breadth_cm ?? ""} onChange={(e) => set("breadth_cm")(e)} data-testid="product-breadth" /></Field>
        <Field label="Height (cm)"><input className={inputCls} type="number" value={form.height_cm ?? ""} onChange={(e) => set("height_cm")(e)} data-testid="product-height" /></Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Overview (long description)">
          <textarea className={inputCls + " min-h-[120px]"} value={form.overview || ""} onChange={set("overview")} />
        </Field>
        <Field label="Philosophy (one-line pull quote)">
          <textarea className={inputCls + " min-h-[120px]"} value={form.philosophy || ""} onChange={set("philosophy")} />
        </Field>
      </div>

      <Field label="Storage instructions">
        <input className={inputCls} value={form.storage || ""} onChange={set("storage")} placeholder="Store in a cool, dry place away from direct sunlight." />
      </Field>

      {/* Homepage flags */}
      <div className="flex items-center gap-6 flex-wrap">
        <label className="flex items-center gap-2 text-[13px] text-[var(--hk-ink-2)]">
          <input type="checkbox" checked={!!form.homepageDisplay} onChange={setBool("homepageDisplay")} className="accent-[var(--hk-orange)]" data-testid="admin-product-homepage-toggle" />
          Display on homepage
        </label>
        <label className="flex items-center gap-2 text-[13px] text-[var(--hk-ink-2)]">
          <input type="checkbox" checked={!!form.featured} onChange={setBool("featured")} className="accent-[var(--hk-orange)]" data-testid="admin-product-featured-toggle" />
          Featured product
        </label>
      </div>

      {/* Benefits */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">Considered benefits</div>
          <button type="button" onClick={addBenefit} className="text-[11px] tracking-widest uppercase text-[var(--hk-orange-deep)] hover:underline">+ Add benefit</button>
        </div>
        <div className="space-y-3">
          {(form.benefits || []).map((b, i) => (
            <div key={i} className="p-4 rounded-lg border border-[var(--hk-border)] bg-white grid md:grid-cols-[1fr_2fr_auto] gap-3 items-start">
              <input className={inputCls} placeholder="Benefit title" value={b.title || ""} onChange={(e) => updateBenefit(i, "title", e.target.value)} />
              <input className={inputCls} placeholder="Short description" value={b.body || ""} onChange={(e) => updateBenefit(i, "body", e.target.value)} />
              <button type="button" onClick={() => removeBenefit(i)} className="p-2 text-[var(--hk-ink-3)] hover:text-red-600" aria-label="Remove benefit">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {(form.benefits || []).length === 0 && <div className="text-[12px] text-[var(--hk-ink-3)] italic">No benefits yet. Add up to 6 for a balanced card layout.</div>}
        </div>
      </div>

      {/* How to Use */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">How to use (ritual steps)</div>
          <button type="button" onClick={addStep} className="text-[11px] tracking-widest uppercase text-[var(--hk-orange-deep)] hover:underline">+ Add step</button>
        </div>
        <div className="space-y-3">
          {(form.howToUse || []).map((s, i) => (
            <div key={i} className="p-4 rounded-lg border border-[var(--hk-border)] bg-white grid md:grid-cols-[80px_1fr_2fr_auto] gap-3 items-start">
              <input className={inputCls} placeholder="01" value={s.step || ""} onChange={(e) => updateStep(i, "step", e.target.value)} />
              <input className={inputCls} placeholder="Step label" value={s.label || ""} onChange={(e) => updateStep(i, "label", e.target.value)} />
              <input className={inputCls} placeholder="Description" value={s.body || ""} onChange={(e) => updateStep(i, "body", e.target.value)} />
              <button type="button" onClick={() => removeStep(i)} className="p-2 text-[var(--hk-ink-3)] hover:text-red-600" aria-label="Remove step">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* FAQs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">FAQs</div>
          <button type="button" onClick={addFaq} className="text-[11px] tracking-widest uppercase text-[var(--hk-orange-deep)] hover:underline">+ Add FAQ</button>
        </div>
        <div className="space-y-3">
          {(form.faqs || []).map((q, i) => (
            <div key={i} className="p-4 rounded-lg border border-[var(--hk-border)] bg-white grid md:grid-cols-[1fr_2fr_auto] gap-3 items-start">
              <input className={inputCls} placeholder="Question" value={q.q || ""} onChange={(e) => updateFaq(i, "q", e.target.value)} />
              <input className={inputCls} placeholder="Answer" value={q.a || ""} onChange={(e) => updateFaq(i, "a", e.target.value)} />
              <button type="button" onClick={() => removeFaq(i)} className="p-2 text-[var(--hk-ink-3)] hover:text-red-600" aria-label="Remove FAQ">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
};

/* ------------------------------------------------------------------ */
/*  Admin Products page                                                */
/* ------------------------------------------------------------------ */
const AdminProductsPage = () => {
  usePageTitle("Admin · Products");
  const pw = sessionStorage.getItem(STORAGE_KEY) || "";
  const { staticProducts, refresh } = useProducts();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("list"); // 'list' | 'create' | 'edit'
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/products`, { headers: { "X-Admin-Password": pw } });
      setRows(res.data || []);
    } catch (e) {
      toast.error("Could not load products. Please sign in via /admin first.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pw) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pw]);

  if (!pw) return null;  // AdminLayout handles auth screen

  const startCreate = () => {
    setEditing(null);
    setMode("create");
  };

  const startEdit = (p) => {
    setEditing(p);
    setMode("edit");
  };

  const cancel = () => {
    setMode("list");
    setEditing(null);
  };

  const save = async (payload) => {
    setBusy(true);
    try {
      if (mode === "edit") {
        await axios.put(`${API}/admin/products/${editing.slug}`, payload, { headers: { "X-Admin-Password": pw } });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: { "X-Admin-Password": pw } });
        toast.success("Product created");
      }
      await load();
      await refresh();
      setMode("list");
      setEditing(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" (${p.slug})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.slug}`, { headers: { "X-Admin-Password": pw } });
      toast.success("Product deleted");
      await load();
      await refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const setStatus = async (p, next) => {
    try {
      await axios.patch(`${API}/admin/products/${p.slug}/status`, { status: next }, { headers: { "X-Admin-Password": pw } });
      toast.success("Status updated");
      await load();
      await refresh();
    } catch (e) {
      toast.error("Status change failed");
    }
  };

  return (
    <AdminLayout
      title="Products"
      subtitle={`${rows.length} products in the catalogue`}
      actions={mode === "list" && (
        <button onClick={startCreate} className="hk-btn-primary h-10 px-4" data-testid="admin-products-create-btn">
          <Plus size={14} /> New Product
        </button>
      )}
    >
      <div className="space-y-8" data-testid="admin-products-page">
        {/* Info card */}
        {mode === "list" && (
          <div className="hk-card p-6 flex items-start gap-4 border-[var(--hk-gold)]/40">
            <div className="w-10 h-10 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-gold)]/40 flex items-center justify-center text-[var(--hk-orange-deep)] shrink-0">
              <Package size={17} strokeWidth={1.6} />
            </div>
            <div>
              <div className="serif text-[18px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>
                Live storefront catalogue
              </div>
              <div className="mt-1 text-[13px] text-[var(--hk-ink-2)] leading-[1.7]">
                Every product below appears on the live website. Add unlimited new products — the storefront lays them out automatically. Uploaded images, prices, stock and content all sync to the public site after Publish.
              </div>
            </div>
          </div>
        )}

        {mode === "list" && (
          <div className="hk-card p-0 overflow-hidden">
            <div className="p-6 flex items-center justify-between gap-4 border-b border-[var(--hk-border)]">
              <div>
                <div className="serif text-[22px] text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>Admin-created products</div>
                <div className="text-[13px] text-[var(--hk-ink-3)] mt-1">{rows.length} in database</div>
              </div>
            </div>

            {loading && <div className="p-8 text-center text-[13px] text-[var(--hk-ink-3)]">Loading…</div>}

            {!loading && rows.length === 0 && (
              <div className="p-12 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-gold)]/40 flex items-center justify-center text-[var(--hk-orange-deep)]">
                  <Package size={20} strokeWidth={1.4} />
                </div>
                <div className="serif mt-5 text-[22px] text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>No products yet.</div>
                <div className="mt-2 text-[14px] text-[var(--hk-ink-2)]">Create your first admin-managed product — it will appear on the storefront the moment you save.</div>
                <button onClick={startCreate} className="hk-btn-primary mt-6" data-testid="admin-products-empty-create">
                  <Plus size={14} /> Create your first product
                </button>
              </div>
            )}

            {!loading && rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-[13.5px]" data-testid="admin-products-table">
                  <thead className="text-[11px] tracking-[0.14em] uppercase text-[var(--hk-ink-3)] border-b border-[var(--hk-border)]">
                    <tr>
                      <th className="text-left px-6 py-3">Product</th>
                      <th className="text-left px-6 py-3">Slug</th>
                      <th className="text-left px-6 py-3">Status</th>
                      <th className="text-left px-6 py-3">Price</th>
                      <th className="text-right px-6 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr key={p.slug} className="border-b border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]/40 transition-colors">
                        <td className="px-6 py-4">
                          <div className="serif text-[16px] text-[var(--hk-ink)]" style={{ paddingBottom: "0.04em" }}>{p.name}</div>
                          {p.tagline && <div className="text-[12px] text-[var(--hk-ink-3)] mt-0.5">{p.tagline}</div>}
                        </td>
                        <td className="px-6 py-4 text-[12.5px] text-[var(--hk-ink-2)]">/products/{p.slug}</td>
                        <td className="px-6 py-4">
                          <select
                            value={p.status}
                            onChange={(e) => setStatus(p, e.target.value)}
                            className="text-[12px] px-2 py-1 rounded border border-[var(--hk-border)] bg-white focus:outline-none focus:ring-1 focus:ring-[var(--hk-orange)]"
                            data-testid={`admin-product-status-${p.slug}`}
                          >
                            {STATUS_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                          </select>
                        </td>
                        <td className="px-6 py-4 text-[13px] text-[var(--hk-ink-2)]">{p.priceDisplay || "—"}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/products/${p.slug}`} target="_blank" className="p-2 rounded-md border border-[var(--hk-border)] hover:border-[var(--hk-orange)] transition-colors text-[var(--hk-ink-2)]" aria-label="View" data-testid={`admin-product-view-${p.slug}`}>
                              <Eye size={14} />
                            </Link>
                            <button onClick={() => startEdit(p)} className="p-2 rounded-md border border-[var(--hk-border)] hover:border-[var(--hk-orange)] transition-colors text-[var(--hk-ink-2)]" aria-label="Edit" data-testid={`admin-product-edit-${p.slug}`}>
                              <Edit3 size={14} />
                            </button>
                            <button onClick={() => remove(p)} className="p-2 rounded-md border border-[var(--hk-border)] hover:border-red-500 hover:text-red-600 transition-colors text-[var(--hk-ink-2)]" aria-label="Delete" data-testid={`admin-product-delete-${p.slug}`}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {(mode === "create" || mode === "edit") && (
          <ProductForm
            initial={editing || emptyProduct}
            isEdit={mode === "edit"}
            onSave={save}
            onCancel={cancel}
            busy={busy}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProductsPage;
